'use client'

import { useAuth } from '@/hooks/useAuth'
import { signOut } from 'next-auth/react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { LogIn, LogOut, Settings, CreditCard, LayoutDashboard } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export function UserMenu() {
	const { isAuthenticated, isLoading, user } = useAuth()
	const t = useTranslations('userMenu')
	const tNav = useTranslations('nav')

	if (isLoading) {
		return <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
	}

	if (!isAuthenticated) {
		return (
			<Link href="/auth/signin">
				<Button variant="default" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
					<LogIn className="mr-2 h-4 w-4" />
					{tNav('signIn')}
				</Button>
			</Link>
		)
	}

	const initials = user?.name
		? user.name
				.split(' ')
				.map((n) => n[0])
				.join('')
				.toUpperCase()
				.slice(0, 2)
		: user?.email?.[0]?.toUpperCase() || '?'

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" className="relative h-8 w-8 rounded-full">
					<Avatar className="h-8 w-8">
						<AvatarImage src={user?.image || undefined} alt={user?.name || ''} />
						<AvatarFallback className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 text-xs">
							{initials}
						</AvatarFallback>
					</Avatar>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent className="w-56" align="end">
				<div className="flex items-center gap-2 p-2">
					<div className="flex min-w-0 flex-col space-y-0.5">
						{user?.name && <p className="truncate text-sm font-medium">{user.name}</p>}
						<p className="truncate text-xs text-muted-foreground">{user?.email}</p>
					</div>
				</div>
				<DropdownMenuSeparator />
				<DropdownMenuItem asChild>
					<Link href="/dashboard">
						<LayoutDashboard className="mr-2 h-4 w-4" />
						{t('dashboard')}
					</Link>
				</DropdownMenuItem>
				<DropdownMenuItem asChild>
					<Link href="/dashboard/settings/billing">
						<CreditCard className="mr-2 h-4 w-4" />
						{t('billing')}
					</Link>
				</DropdownMenuItem>
				<DropdownMenuItem asChild>
					<Link href="/dashboard/settings/account">
						<Settings className="mr-2 h-4 w-4" />
						{t('settings')}
					</Link>
				</DropdownMenuItem>
				<DropdownMenuSeparator />
				<DropdownMenuItem onClick={() => signOut({ callbackUrl: '/' })}>
					<LogOut className="mr-2 h-4 w-4" />
					{t('signOut')}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
